import React, { useState } from "react";
import {
  ChevronDownIcon,
  Squares2X2Icon,
  CogIcon,
  LockClosedIcon,
  Bars3Icon,
  CalendarIcon,
  PhoneIcon,
} from "@heroicons/react/24/solid";
import { Menu, Transition } from "@headlessui/react";
import Section from "./Section";
import { Link } from "./../util/router";
import Button from "./Button";
import { useAuth } from "./../util/auth";
import logo from "./../assets/logo.jpg";

function Navbar(props) {
  const auth = useAuth();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const classes = {
    navLink:
      "font-semibold inline-flex items-center space-x-1 h-8 px-4 group-hover:text-blue-500 py-6",
    navLinkIcon:
      "opacity-50 transform transition duration-200 ease-out group-hover:rotate-180 inline-block w-4 h-4",
    accountDropdown: {
      base: "absolute right-0 origin-top-right mt-2 w-48 z-10",
      inner:
        "bg-white ring-1 ring-black ring-opacity-5 rounded divide-y divide-gray-100 shadow-xl rounded",
      link: "flex items-center space-x-2 rounded py-2 px-3 text-sm font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-700",
      linkActive: "text-gray-700 bg-gray-100",
      linkInactive:
        "text-gray-600 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:bg-gray-100 focus:text-gray-700",
      icon: "opacity-50 inline-block w-5 h-5",
    },
  };

  return (
    <Section bgColor={props.bgColor}>
      <div className="container py-4">
        <div className="flex flex-col lg:flex-row lg:justify-between">
          <div className="flex justify-between items-center mb-4 lg:mb-0">
            <Link
              to="/"
              className="inline-flex items-center space-x-2 font-bold text-lg tracking-wide text-blue-600 hover:text-blue-400"
            >
              <img 
                src={logo} 
                alt="San Mateo Sushi Logo" 
                className="h-10 w-auto"
              />
              <span className="hidden sm:inline">Sushi Edomata</span>
            </Link>
            <div className="flex lg:hidden items-center justify-center">
              <Button
                variant="simple"
                size="sm"
                onClick={() => setMobileNavOpen(!mobileNavOpen)}
                endIcon={<Bars3Icon className="inline-block w-5 h-5" />}
              />
            </div>
          </div>
          <div className="flex items-center space-x-1 lg:space-x-4">
            <ul className="hidden lg:flex items-center ml-auto">
              <li className="relative group">
                <Link to="/" className={`${classes.navLink}`}>
                  <span>Make Reservation</span>
                </Link>
              </li>
              <li className="relative group">
                <Link to="/my-reservations" className={`${classes.navLink}`}>
                  <span>My Reservations</span>
                </Link>
              </li>
              {!auth.user && (
                <li className="relative group">
                  <Link to="/auth/signin" className={`${classes.navLink}`}>
                    <span>Sign in</span>
                  </Link>
                </li>
              )}
            </ul>

            {auth.user && (
              <div className="hidden lg:block">
                <Menu as="div" className="relative inline-block">
                  <Button
                    component={Menu.Button}
                    variant="simple"
                    size="sm"
                    endIcon={
                      <ChevronDownIcon className="opacity-50 inline-block w-5 h-5" />
                    }
                  >
                    Account
                  </Button>
                  <Transition
                    as={React.Fragment}
                    enter="transition ease-out duration-150"
                    enterFrom="transform opacity-0 scale-75"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-100"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-75"
                  >
                    <Menu.Items className={classes.accountDropdown.base}>
                      <div className={`${classes.accountDropdown.inner}`}>
                        <div className="p-2 space-y-1">
                          <Menu.Item>
                            <Link
                              to="/settings/general"
                              className={`${classes.accountDropdown.link}`}
                            >
                              <CogIcon className={classes.accountDropdown.icon} />
                              <span>Settings</span>
                            </Link>
                          </Menu.Item>
                          {auth.user && auth.user.email === "sanmateosushi.reservations@gmail.com" && (
                            <Menu.Item>
                              <Link
                                to="/admin"
                                className={`${classes.accountDropdown.link}`}
                              >
                                <LockClosedIcon className={classes.accountDropdown.icon} />
                                <span>Admin</span>
                              </Link>
                            </Menu.Item>
                          )}
                        </div>
                        <div className="p-2 space-y-1">
                          <Link
                            to="#"
                            className={`${classes.accountDropdown.link}`}
                            onClick={(e) => {
                              e.preventDefault();
                              auth.signout();
                            }}
                          >
                            <LockClosedIcon
                              className={classes.accountDropdown.icon}
                            />
                            <span>Sign out</span>
                          </Link>
                        </div>
                      </div>
                    </Menu.Items>
                  </Transition>
                </Menu>
              </div>
            )}
          </div>
        </div>
        <div className={"lg:hidden" + (!mobileNavOpen ? " hidden" : "")}>
          <div className="bg-white border rounded-xl my-5 shadow-lg">
            <div className="p-6 space-y-6">
              <nav className="flex flex-col space-y-4">
                <Link
                  to="/"
                  className={`${classes.accountDropdown.link} text-base`}
                >
                  <CalendarIcon className="w-5 h-5 mr-2" />
                  Make Reservation
                </Link>
                <Link
                  to="/my-reservations"
                  className={`${classes.accountDropdown.link} text-base`}
                >
                  <Squares2X2Icon className="w-5 h-5 mr-2" />
                  My Reservations
                </Link>
                <a
                  href="tel:650-344-0888"
                  className={`${classes.accountDropdown.link} text-base`}
                >
                  <PhoneIcon className="w-5 h-5 mr-2" />
                  Call Us: 650-344-0888
                </a>
                {!auth.user && (
                  <Link
                    to="/auth/signin"
                    className={`${classes.accountDropdown.link} text-base`}
                  >
                    <LockClosedIcon className="w-5 h-5 mr-2" />
                    Sign in
                  </Link>
                )}
                {auth.user && (
                  <>
                    <Link
                      to="/settings/general"
                      className={`${classes.accountDropdown.link} text-base`}
                    >
                      <CogIcon className="w-5 h-5 mr-2" />
                      Settings
                    </Link>
                    {auth.user && auth.user.email === "sanmateosushi.reservations@gmail.com" && (
                      <Link
                        to="/admin"
                        className={`${classes.accountDropdown.link} text-base`}
                      >
                        <LockClosedIcon className="w-5 h-5 mr-2" />
                        Admin
                      </Link>
                    )}
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        auth.signout();
                      }}
                      className={`${classes.accountDropdown.link} text-base w-full text-left`}
                    >
                      <LockClosedIcon className="w-5 h-5 mr-2" />
                      Sign out
                    </button>
                  </>
                )}
              </nav>
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
}

export default Navbar;
